import axios from 'axios';

// In production (Railway), env vars point to the real domain — use them directly.
// In dev, CRA bakes "localhost" into the bundle at startup, which breaks access
// from other devices on the network. Swap localhost/127.0.0.1 with the actual
// hostname of the machine so any device on the LAN hits the right server.
const _resolveUrl = (envValue, fallback) => {
  const url = envValue || fallback;
  if (!url.includes('localhost') && !url.includes('127.0.0.1')) return url;
  return url
    .replace('localhost', window.location.hostname)
    .replace('127.0.0.1', window.location.hostname);
};

export const API_BASE_URL = _resolveUrl(process.env.REACT_APP_API_URL, 'http://localhost:8000/api');
const IMAGE_BASE_URL = _resolveUrl(process.env.REACT_APP_BACKEND_URL, 'http://localhost:8000');

export const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${IMAGE_BASE_URL}${path}`;
};

// Read a cookie value by name (for CSRF double-submit pattern)
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Create axios instance — withCredentials sends HttpOnly auth cookies automatically
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ── Request interceptor ────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    // Backward-compat: also send token in header if it is stored locally
    const token = localStorage.getItem('lxi_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Double-submit CSRF cookie for all state-changing requests
    const method = (config.method || '').toLowerCase();
    if (['post', 'put', 'delete', 'patch'].includes(method)) {
      const csrf = getCookie('csrf_token');
      if (csrf) config.headers['X-CSRF-Token'] = csrf;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Pages where a 401 should NOT trigger a redirect to /login
// (public auth pages that are legitimately accessible without a session)
const NO_REDIRECT_PATHS = ['/login', '/register', '/forgot-password', '/reset-password'];

// ── Response interceptor ───────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const hadSession = !!localStorage.getItem('lxi_token');
      localStorage.removeItem('lxi_token');
      localStorage.removeItem('lxi_user');
      const onPublicPath = NO_REDIRECT_PATHS.some(
        (p) => window.location.pathname.startsWith(p)
      );
      if (hadSession && !onPublicPath) {
        window.location.href = '/login?expired=1';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================
// AUTHENTICATION
// ============================================================

export const authService = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  getCurrentUser: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetOtp: (email, otp) => api.post('/auth/verify-reset-otp', { email, otp }),
  resetPassword: (email, otp, new_password) => api.post('/auth/reset-password', { email, otp, new_password }),
  verifyResetToken: (token) => api.post('/auth/verify-reset-token', { token }),
  resetPasswordWithToken: (token, new_password) => api.post('/auth/reset-password-with-token', { token, new_password }),
  verifyUnlock: (email, otp) => api.post('/auth/verify-unlock', { email, otp }),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
};

// ============================================================
// USER PROFILE
// ============================================================

export const userService = {
  getProfile: (userId) => api.get(`/users/${userId}`),
  updateProfile: (data) => api.put('/users/profile', data),
};

// ============================================================
// PRODUCTS
// ============================================================

export const productService = {
  getAll: (params) => api.get('/products', { params }),
  getAllAdmin: () => api.get('/admin/products'),
  getById: (productId) => api.get(`/products/${encodeURIComponent(productId)}`),
  getBySlug: (slug) => api.get(`/products/slug/${encodeURIComponent(slug)}`),
  create: (data) => api.post('/admin/products', data),
  update: (productId, data) => api.put(`/admin/products/${productId}`, data),
  delete: (productId) => api.delete(`/admin/products/${productId}`),
};

// ============================================================
// WISHLIST
// ============================================================

export const wishlistService = {
  getWishlist: () => api.get('/wishlist'),
  addItem: (productId) => api.post(`/wishlist/${productId}`),
  removeItem: (productId) => api.delete(`/wishlist/${productId}`),
};

// ============================================================
// REVIEWS
// ============================================================

export const reviewService = {
  create: (data) => api.post('/reviews', data),
  getByProduct: (productId, params) => api.get(`/products/${productId}/reviews`, { params }),
  update: (reviewId, data) => api.put(`/reviews/${reviewId}`, data),
  delete: (reviewId) => api.delete(`/reviews/${reviewId}`),
};

// ============================================================
// ORDERS
// ============================================================

export const orderService = {
  createCheckout: (data) => api.post('/checkout/create-session', data),
  createPayPalCheckout: (data) => api.post('/checkout/create-paypal-order', data),
  capturePayPalOrder: (orderId) => api.post('/checkout/capture-paypal-order', { order_id: orderId }),
  getStatus: (sessionId) => api.get(`/checkout/status/${sessionId}`),
  getOrders: () => api.get('/orders'),
  getMyOrders: () => api.get('/orders'),
  getOrder: (sessionId) => api.get(`/orders/${sessionId}`),
};

// ============================================================
// NEWSLETTER
// ============================================================

export const newsletterService = {
  subscribe: (email) => api.post('/newsletter/subscribe', { email }),
};

// ============================================================
// ADMIN
// ============================================================

export const adminService = {
  getAllOrders: () => api.get('/admin/orders'),
  getSubscribers: () => api.get('/admin/newsletter/subscribers'),
  getIntegrationStatus: () => api.get('/admin/integrations/status'),
  getHomeContent: () => api.get('/admin/site-content/home'),
  getGlobalContent: () => api.get('/admin/site-content/global'),
  getPhilosophyContent: () => api.get('/admin/site-content/philosophy'),
  updateHomeContent: (data) => api.put('/admin/site-content/home', data),
  updateGlobalContent: (data) => api.put('/admin/site-content/global', data),
  updatePhilosophyContent: (data) => api.put('/admin/site-content/philosophy', data),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/admin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  syncPrintful: () => api.post('/admin/sync-products-printful'),
  importPrintfulCatalog: () => api.post('/admin/import-printful-catalog'),
  getStats: () => api.get('/admin/stats'),
};

export const siteContentService = {
  getHomeContent: () => api.get('/site-content/home'),
  getGlobalContent: () => api.get('/site-content/global'),
  getPhilosophyContent: () => api.get('/site-content/philosophy'),
};

// ============================================================
// PRODUCT NORMALIZATION
// ============================================================

export function normalizeProduct(p) {
  return {
    ...p,
    id: p._id || p.id,
    name: p.name || p.title || '',
    price: p.price || p.variants?.[0]?.retail_price || p.printful_variants?.[0]?.retail_price || 0,
    image: p.images?.[0] || p.image_url || p.image || '',
    images: p.images?.length > 0 ? p.images : (p.image_url ? [p.image_url] : []),
    sizes: p.sizes || [],
  };
}

export default api;
